import { gql } from '@apollo/client';

export const GET_NOTE = gql`
  query Note($noteId: ID!) {
    note(noteId: $noteId) {
      id
      title
      formattedMessage
      approvedAt
      status
      approvedBy {
        id
        name
      }
      editedBy {
        id
        name
      }
      createdBy {
        id
        name
      }
    }
  }
`;

export const CHAT_MUTATION = gql`
  mutation Chat($input: ChatInput!) {
    chat(input: $input) {
      type
      text
      conversationId
      letterHtml
      citations {
        filename
        filepath
        extension
        documentType
        resourceId
        pageNumber
        documentDate
        authorName
        status
        snippets {
          content
          page
        }
      }
    }
  }
`;

export const CONVERSATIONS_QUERY = gql`
  query Conversations($patientId: Int!) {
    conversations(patientId: $patientId) {
      id
      title
      createdAt
      updatedAt
    }
  }
`;

export const CONVERSATION_QUERY = gql`
  query Conversation($conversationId: Int!) {
    conversation(conversationId: $conversationId) {
      id
      title
      createdAt
      updatedAt
      messages {
        id
        role
        content
        createdAt
        responseData
        citations {
          filename
          filepath
          extension
          documentType
          resourceId
          pageNumber
          authorName
          status
          documentDate
          snippets {
            content
            page
          }
        }
      }
    }
  }
`;

export const DELETE_CONVERSATION_MUTATION = gql`
  mutation DeleteConversation($conversationId: Int!) {
    deleteConversation(conversationId: $conversationId) {
      success
    }
  }
`;

export const RENAME_CONVERSATION_MUTATION = gql`
  mutation RenameConversation($conversationId: Int!, $title: String!) {
    renameConversation(conversationId: $conversationId, title: $title) {
      id
      title
    }
  }
`;

export const AI_TIMELINE_CITATION_FRAGMENT = gql`
  fragment AiPatientTimelineCitationObjectTypeFragment on AiPatientTimelineCitationObjectType {
    title
    documentType
    documentUrl
    dateOfDocument
    status
    notes
    citedText
    authorName
    highlightGeoms {
      pageNumber
      geometry {
        type
        coordinates
      }
    }
  }
`;

export const AI_TIMELINE_FRAGMENT = gql`
  ${AI_TIMELINE_CITATION_FRAGMENT}
  fragment AiPatientTimelineSummaryObjectTypeFragment on AiPatientTimelineSummaryObjectType {
    text
    citations {
      ...AiPatientTimelineCitationObjectTypeFragment
    }
    subItems {
      text
      citations {
        ...AiPatientTimelineCitationObjectTypeFragment
      }
    }
  }
`;

export const AI_SUMMARY_CITATION_FRAGMENT = gql`
  fragment AiPatientSummaryCitationObjectTypeFragment on AiPatientSummaryCitationObjectType {
    resourceType
    resourceId
    citedText
    resourceName
    title
    dateOfDocument
    page
    authorName
    status
    highlightGeoms {
      pageNumber
      geometry {
        type
        coordinates
      }
    }
  }
`;

export const AI_SUMMARY_FRAGMENT = gql`
  ${AI_SUMMARY_CITATION_FRAGMENT}
  fragment AiPatientSummaryGenericObjectTypeFragment on AiPatientSummaryGenericObjectType {
    text
    citations {
      ...AiPatientSummaryCitationObjectTypeFragment
    }
    subItems {
      text
      citations {
        ...AiPatientSummaryCitationObjectTypeFragment
      }
    }
  }
`;

export const AI_SUMMARY_QUERY = gql`
  ${AI_SUMMARY_CITATION_FRAGMENT}
  ${AI_SUMMARY_FRAGMENT}
  query AiSummary($patientId: ID!) {
    aiSummary(patientId: $patientId) {
      patientId
      updatedAt
      traceId
      spanId
      summary {
        overview {
          standard {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
          compact {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
        }
        cancerHistory {
          standard {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
          compact {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
        }
        medicalHistory {
          standard {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
          compact {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
        }
        familyHistory {
          standard {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
          compact {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
        }
        socialHistory {
          standard {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
          compact {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
        }
        medications {
          standard {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
          compact {
            ...AiPatientSummaryGenericObjectTypeFragment
          }
        }
      }
    }
  }
`;

export const USER_ROLE_QUERY = gql`
  {
    user {
      id
      isSuperuser
    }
  }
`;

export const AI_TIMELINE_QUERY = gql`
  ${AI_TIMELINE_CITATION_FRAGMENT}
  ${AI_TIMELINE_FRAGMENT}
  query AiTimeline($patientId: ID!) {
    aiTimeline(patientId: $patientId) {
      timeline {
        type
        date
        title
        isLatest
        isNew
        resourceId
        traceId
        spanId
        standard {
          ...AiPatientTimelineSummaryObjectTypeFragment
        }
        compact {
          ...AiPatientTimelineSummaryObjectTypeFragment
        }
      }
    }
  }
`;

// AI Letter operations
export const AI_LETTER_QUERY = gql`
  query AiLetter($patientId: Int!, $conversationId: Int, $letterType: String!) {
    aiLetter(patientId: $patientId, conversationId: $conversationId, letterType: $letterType) {
      id
      patientId
      conversationId
      letterType
      contentHtml
      isSaved
      createdAt
      updatedAt
      emrDocumentId
      version
    }
  }
`;

export const UPSERT_AI_LETTER_MUTATION = gql`
  mutation UpsertAiLetter($input: UpsertAiLetterInput!) {
    upsertAiLetter(input: $input) {
      id
      patientId
      conversationId
      letterType
      contentHtml
      isSaved
      createdAt
      updatedAt
      emrDocumentId
      version
    }
  }
`;

export const SEND_AI_LETTER_TO_FHIR_MUTATION = gql`
  mutation SendAiLetterToFhir($input: SendAiLetterToFhirInput!) {
    sendAiLetterToFhir(input: $input) {
      success
      message
    }
  }
`;

// AI Config query
export const AI_CONFIG_QUERY = gql`
  query AiConfig {
    aiConfig {
      hasDocumentGenerationFeature
    }
  }
`;
